// Per-dialect synthesizer for CREATE / ALTER / DROP PACKAGE [BODY]
// (v2 Slice D8).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D8.
//
// Oracle-only — every other engine rejects with a documented redirect (no
// equivalent — packages are an oracle-only construct). Shell-only — `bodyText`
// is opaque verbatim text the resolver does not re-parse (PL/SQL grammar owns
// the content). The synthesizer emits the engine-specific prologue (CREATE
// keywords, name) and concatenates `bodyText` byte-for-byte after the `AS`
// separator.
//
// Per-engine surface:
//   - oracle: CREATE [OR REPLACE] [EDITIONABLE|NONEDITIONABLE] PACKAGE [BODY]
//             <name> AS <bodyText>
//             ALTER PACKAGE <name> COMPILE [BODY]
//             DROP PACKAGE [BODY] <name>
//   - other engines: not supported — synthesizer throws.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { PackageChange, PackageShellDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

function nameText(engine: SqlEngine, name: QualifiedName): string {
  if (name.raw && name.raw.length > 0) return name.raw;
  const bare = maybeQuoteIdent(engine, name.name, name.quoted);
  if (name.schema) {
    const schema = maybeQuoteIdent(engine, name.schema, name.quoted);
    return `${schema}.${bare}`;
  }
  return bare;
}

function unsupported(engine: SqlEngine, op: string): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support ${op} — `
      + `packages are an oracle-only construct. Move shared logic to schemas + `
      + `routines, or compose multiple ${op === 'create-package' ? 'CREATE FUNCTION / PROCEDURE' : 'object-level'} calls.`,
  );
}

function validateBody(p: PackageShellDef): void {
  if (p.bodyText.trim().length === 0) {
    throw new Error(
      `applyDdlEdit: create-package requires a non-empty bodyText (the package `
        + `declarations or body, including the trailing END clause).`,
    );
  }
}

function packageKindKeyword(kind: PackageShellDef['kind']): string {
  return kind === 'body' ? 'PACKAGE BODY' : 'PACKAGE';
}

// ─── CREATE PACKAGE ───────────────────────────────────────────────────────

export function synthesizeCreatePackage(
  engine: SqlEngine,
  pkg: PackageShellDef,
): string {
  if (engine !== 'oracle') unsupported(engine, 'create-package');
  if (pkg.oracleEditionable && pkg.oracleNonEditionable) {
    throw new Error(
      `applyDdlEdit: oracle create-package: 'oracleEditionable' and `
        + `'oracleNonEditionable' are mutually exclusive`,
    );
  }
  validateBody(pkg);
  let s = pkg.orReplace ? 'CREATE OR REPLACE ' : 'CREATE ';
  if (pkg.oracleEditionable) s += 'EDITIONABLE ';
  if (pkg.oracleNonEditionable) s += 'NONEDITIONABLE ';
  s += `${packageKindKeyword(pkg.kind)} ${nameText('oracle', pkg.name)}`;
  s += ` AS ${pkg.bodyText.trim()}`;
  return s;
}

// ─── ALTER PACKAGE (compile only) ─────────────────────────────────────────

export function synthesizeAlterPackage(
  engine: SqlEngine,
  packageName: QualifiedName,
  change: PackageChange,
): string {
  if (engine !== 'oracle') unsupported(engine, 'alter-package');
  if (!change.recompile) {
    throw new Error(
      `applyDdlEdit: oracle alter-package requires 'recompile: true' — `
        + `oracle's only portable ALTER PACKAGE shape is COMPILE [BODY]. `
        + `Other ALTER changes (e.g. RENAME) require a separate DDL surface.`,
    );
  }
  if (change.compileBody === true) {
    return `ALTER PACKAGE ${nameText('oracle', packageName)} COMPILE BODY`;
  }
  return `ALTER PACKAGE ${nameText('oracle', packageName)} COMPILE`;
}

// ─── DROP PACKAGE [BODY] ──────────────────────────────────────────────────

export function synthesizeDropPackage(
  engine: SqlEngine,
  packageName: QualifiedName,
  packageKind: PackageShellDef['kind'] | undefined,
  ifExists: boolean,
): string {
  if (engine !== 'oracle') unsupported(engine, 'drop-package');
  if (ifExists) {
    throw new Error(
      `applyDdlEdit: oracle drop-package does not accept 'ifExists' — `
        + `oracle DROP PACKAGE has no IF EXISTS keyword. Wrap in PL/SQL with `
        + `USER_OBJECTS lookup at the consumer level.`,
    );
  }
  if (packageKind === 'body') {
    return `DROP PACKAGE BODY ${nameText('oracle', packageName)}`;
  }
  return `DROP PACKAGE ${nameText('oracle', packageName)}`;
}
